/**
 * Excerpt operations shared by the icon menu, the Excerpts panel and the
 * Tagged Items list (#277). Each refreshes the in-tab store afterwards so
 * marks and embeds update without a reload.
 */
import type { ExcerptStore } from '../../contexts/ExcerptSyncContext';
import {
  addDestination, addNextMeeting, deleteExcerpt, removeDestination, isSourceType, nextMeetingName, nextMeetingUrl, NEXT_MEETING,
  type ExcerptView, type ItemRef, type NextMeeting, type SourceType,
} from '../../lib/excerpts';
import type { PickedItem } from './DestinationPicker';

/** Client route for an item. Meetings open by id through the standalone route, which loads any instance. */
export function itemUrl(type: string, id: string): string {
  switch (type) {
    case 'task': return `/tasks/${id}`;
    case 'note': return `/notes/${id}`;
    case 'meeting_instance': return `/meetings/standalone/${id}`;
    case 'meeting_series': return `/meetings/${id}`;
    case 'scratchpad': return `/scratchpad/${id}`;
    case 'scope': return `/projects/${id}`;
    case 'reference_doc': return `/views/${id}`;
    case 'person': return `/people/${id}`;
    case 'tag': return '/tags';
    default: return '/';
  }
}

/** The source page, scrolled to the excerpt (DossierEditor reads the hash). */
export const sourceUrl = (v: ExcerptView) => `${itemUrl(v.source.type, v.source.id)}#excerpt-${v.id}`;

function refresh(store: ExcerptStore | null, v: ExcerptView) {
  if (!store) return;
  store.invalidate([v.id]);
  if (isSourceType(v.source.type)) void store.refreshSource({ type: v.source.type as SourceType, id: v.source.id });
}

/**
 * #360: the toast after sending to a series' next meeting - which meeting it
 * went to (and whether sending created it), with a link to open it.
 */
export function nextMeetingToast(
  toast: { success: (message: string, action?: { label: string; onClick: () => void }) => void },
  reached: NextMeeting[],
  open: ((url: string) => void) | null,
  otherCount = 0,
): boolean {
  if (reached.length === 0) return false;
  const action = reached.length === 1 && open ? { label: 'Open', onClick: () => open(nextMeetingUrl(reached[0])) } : undefined;
  if (reached.length === 1 && otherCount === 0) {
    toast.success(`Excerpt sent to ${nextMeetingName(reached[0])}${reached[0].created ? ' (new meeting)' : ''}`, action);
    return true;
  }
  const made = reached.filter((m) => m.created).length;
  toast.success(`Excerpt sent to ${reached.length + otherCount} items${made ? `, ${made} new meeting${made === 1 ? '' : 's'}` : ''}`, action);
  return true;
}

/**
 * Applies a new destination set. Bodies open in this tab get their embed from
 * their own editor (`placed`), so their autosave and this request never clash.
 * Returns the meetings any next-meeting destinations reached (#360).
 */
export async function changeDestinations(store: ExcerptStore | null, v: ExcerptView, picked: PickedItem[]): Promise<NextMeeting[]> {
  const key = (d: { type: string; id: string }) => `${d.type}:${d.id}`;
  const before = new Set(v.destinations.map(key));
  const after = new Set(picked.map(key));
  const reached: NextMeeting[] = [];
  for (const p of picked) {
    if (p.type === NEXT_MEETING) {
      // The meeting the picker expected: when it is open here, its editor places the embed.
      const editor = p.meetingId ? store?.editorFor({ type: 'meeting_instance', id: p.meetingId }) : undefined;
      const placed = !!editor && editor.commands.insertExcerptEmbed(v.id);
      const out = await addNextMeeting(v.id, p.id, { date: p.date, placedMeetingId: placed ? p.meetingId : undefined });
      reached.push(out.next_meeting);
      after.add(key({ type: 'meeting_instance', id: out.next_meeting.id }));
      continue;
    }
    if (before.has(key(p))) continue;
    const editor = store?.editorFor(p);
    const placed = !!editor && editor.commands.insertExcerptEmbed(v.id);
    await addDestination(v.id, { type: p.type, id: p.id }, placed);
  }
  for (const d of v.destinations) {
    if (!after.has(key(d))) await removeDestination(v.id, { type: d.type as ItemRef['type'], id: d.id });
  }
  refresh(store, v);
  return reached;
}

export async function removeFrom(store: ExcerptStore | null, v: ExcerptView, dest: { type: string; id: string }): Promise<{ excerptNowEmpty: boolean }> {
  const out = await removeDestination(v.id, { type: dest.type as ItemRef['type'], id: dest.id });
  refresh(store, v);
  return out;
}

export async function removeExcerpt(store: ExcerptStore | null, v: ExcerptView, deleteLinked: boolean): Promise<void> {
  await deleteExcerpt(v.id, deleteLinked);
  refresh(store, v);
}
